import React from 'react';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';

export interface PackageComponent {
  id: string;
  component_type: string;
  name: string;
  quantity: number;
  unit_price_dollars: number;
  sort_order: number;
  session_length_minutes?: number | null;
  session_link?: string | null;
}

export interface PackageFormData {
  name: string;
  description: string;
  duration_months: number;
  components: PackageComponent[];
}

const LINK_ENABLED_GUIDE_ID = '47e9e849-d537-4668-afa6-8ff6123dc520';

interface PackageFormProps {
  formData: PackageFormData;
  setFormData: (data: PackageFormData) => void;
  isEditing: boolean;
  onSave: () => void;
  onCancel: () => void;
  loading?: boolean;
  readOnly?: boolean;
  guideId?: string | null;
}

const PackageForm: React.FC<PackageFormProps> = ({
  formData,
  setFormData,
  isEditing,
  onSave,
  onCancel,
  loading = false,
  readOnly = false,
  guideId
}) => {
  const showLinkField = guideId === LINK_ENABLED_GUIDE_ID;
  const isReadOnly = readOnly;
  const componentTypes = ['preparation', 'ceremony', 'integration', 'additional'];

  const addComponent = () => {
    if (isReadOnly) return;
    const newComponent: PackageComponent = {
      id: '',
      component_type: 'additional',
      name: '',
      quantity: 1,
      unit_price_dollars: 0,
      sort_order: (formData.components.length + 1) * 10,
      session_length_minutes: 60
    };
    setFormData({
      ...formData,
      components: [...formData.components, newComponent]
    });
  };

  const updateComponent = (index: number, field: keyof PackageComponent, value: any) => {
    if (isReadOnly) return;
    const updatedComponents = [...formData.components];
    updatedComponents[index] = { ...updatedComponents[index], [field]: value };
    setFormData({
      ...formData,
      components: updatedComponents
    });
  };

  const removeComponent = (index: number) => {
    if (isReadOnly) return;
    setFormData({
      ...formData,
      components: formData.components.filter((_, i) => i !== index)
    });
  };

  const calculateTotalCost = () => {
    return formData.components.reduce((total, component) => {
      return total + (component.unit_price_dollars * component.quantity);
    }, 0);
  };

  const getComponentIcon = (type: string) => {
    switch (type) {
      case 'preparation': return '/clipboard.svg';
      case 'ceremony': return '/star.svg';
      case 'integration': return '/heart.svg';
      case 'additional': return '/plus.svg';
      default: return '•';
    }
  };

  const formatComponentType = (type: string) => {
    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  // Helper to convert total minutes to hours and minutes
  const minutesToHoursAndMinutes = (totalMinutes: number | null | undefined): { hours: number; minutes: number } => {
    const total = totalMinutes ?? 60;
    return {
      hours: Math.floor(total / 60),
      minutes: total % 60
    };
  };

  // Helper to update session length from hours/minutes inputs
  const updateSessionLength = (index: number, hours: number, minutes: number) => {
    const totalMinutes = (hours * 60) + Math.min(59, Math.max(0, minutes));
    updateComponent(index, 'session_length_minutes', totalMinutes);
  };

  return (
    <div className="bg-[#FFFCEF] rounded-[32px] p-5">
      <div className="bg-white/85 border border-[#2C2731]/15 rounded-3xl p-6 shadow-[0_24px_60px_-40px_rgba(32,26,45,0.6)] text-[#2C2731]">
      <h2 className="text-xl font-semibold text-[#2C2731] mb-4">
        {isReadOnly ? 'Package Details' : isEditing ? 'Edit Package' : 'Create New Package'}
      </h2>

      {/* Basic Info - Horizontal Layout */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <div>
          <label className="block text-xs font-semibold tracking-wide text-[#2C2731]/80 mb-1">Package Name</label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="Enter package name"
            disabled={isReadOnly}
            className="w-full rounded-2xl border border-[#2C2731]/20 bg-white px-4 py-3 text-sm placeholder:text-[#2C2731]/40 focus:border-[#2C2731] focus:outline-none focus:ring-1 focus:ring-[#2C2731] disabled:bg-[#F4EED3] disabled:text-[#2C2731]/50"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold tracking-wide text-[#2C2731]/80 mb-1">Description</label>
          <input
            type="text"
            value={formData.description || ''}
            onChange={(e) => setFormData({ ...formData, description: e.target.value.slice(0, 500) })}
            placeholder="Brief description of the package"
            maxLength={500}
            disabled={isReadOnly}
            className="w-full rounded-2xl border border-[#2C2731]/20 bg-white px-4 py-3 text-sm placeholder:text-[#2C2731]/40 focus:border-[#2C2731] focus:outline-none focus:ring-1 focus:ring-[#2C2731] disabled:bg-[#F4EED3] disabled:text-[#2C2731]/50"
          />
          {!isReadOnly && (
            <p className={`mt-1 text-right text-xs ${(formData.description || '').length >= 450 ? 'text-[#B24E4E]' : 'text-[#2C2731]/50'}`}>
              {(formData.description || '').length}/500
            </p>
          )}
        </div>
        <div>
          <label className="block text-xs font-semibold tracking-wide text-[#2C2731]/80 mb-1">Program Length</label>
          <select
            value={formData.duration_months}
            onChange={(e) => setFormData({ ...formData, duration_months: parseInt(e.target.value) })}
            disabled={isReadOnly}
            className="w-full rounded-2xl border border-[#2C2731]/20 bg-white px-4 py-3 text-sm focus:border-[#2C2731] focus:outline-none focus:ring-1 focus:ring-[#2C2731] disabled:bg-[#F4EED3] disabled:text-[#2C2731]/50"
          >
            {Array.from({ length: 24 }, (_, i) => i + 1).map((month) => (
              <option key={month} value={month}>
                {month} {month === 1 ? 'Month' : 'Months'}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Package Components */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-[#2C2731]">Package Components</h3>
          {!isReadOnly && (
            <button
              onClick={addComponent}
              className="inline-flex items-center rounded-full border border-[#2C2731]/25 px-3 py-1.5 text-xs text-[#2C2731] hover:bg-[#FFFCEF] transition-colors"
            >
              <PlusIcon className="w-3 h-3 mr-1" />
              Add
            </button>
          )}
        </div>

        {/* Component Types Legend */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3 p-3 bg-white/70 border border-[#2C2731]/10 rounded-2xl">
          {componentTypes.map((type) => (
            <div key={type} className="flex items-center text-xs text-[#2C2731]/70">
              <span className="mr-1"><img src={getComponentIcon(type)} className="w-3 h-3" /></span>
              <span>{formatComponentType(type)}</span>
            </div>
          ))}
        </div>

        {/* Components List */}
        <div className="space-y-2">
          {formData.components.map((component, index) => (
            <div key={component.id || index} className="rounded-2xl border border-[#2C2731]/15 bg-white/75 p-3">
              {/* Mobile: Stack vertically, Desktop: Single row */}
              <div className="flex flex-col gap-3 md:flex-row md:flex-wrap md:items-center md:gap-2">
                {/* Row 1 on mobile: Type and Name */}
                <div className="flex gap-2 w-full md:w-auto md:flex-1">
                  <select
                    value={component.component_type}
                    onChange={(e) => updateComponent(index, 'component_type', e.target.value)}
                    disabled={isReadOnly}
                    className="w-28 shrink-0 px-3 py-2 rounded-xl border border-[#2C2731]/20 bg-white text-xs text-[#2C2731] focus:border-[#2C2731] focus:outline-none disabled:bg-[#F4EED3] disabled:text-[#2C2731]/50"
                  >
                    {componentTypes.map((type) => (
                      <option key={type} value={type}>{formatComponentType(type)}</option>
                    ))}
                  </select>
                  <input
                    type="text"
                    value={component.name || ''}
                    onChange={(e) => updateComponent(index, 'name', e.target.value)}
                    placeholder="Component name"
                    disabled={isReadOnly}
                    className="flex-1 min-w-0 px-3 py-2 rounded-xl border border-[#2C2731]/20 bg-white text-xs focus:border-[#2C2731] focus:outline-none disabled:bg-[#F4EED3] disabled:text-[#2C2731]/50"
                  />
                </div>
                {/* Row 2 on mobile: Price, Qty, Duration, Delete */}
                <div className="flex flex-wrap items-center gap-2">
                  <div className="flex items-center">
                    <span className="text-xs text-[#2C2731]/70 mr-1">$</span>
                    <input
                      type="number"
                      value={component.unit_price_dollars}
                      onChange={(e) => updateComponent(index, 'unit_price_dollars', parseInt(e.target.value) || 0)}
                      placeholder="Cost"
                      min="0"
                      disabled={isReadOnly}
                      className="w-20 px-3 py-2 rounded-xl border border-[#2C2731]/20 bg-white text-xs focus:border-[#2C2731] focus:outline-none disabled:bg-[#F4EED3] disabled:text-[#2C2731]/50"
                    />
                  </div>
                  <div className="flex items-center">
                    <span className="text-xs text-[#2C2731]/70 mr-1">Qty</span>
                    <input
                      type="number"
                      value={component.quantity}
                      onChange={(e) => updateComponent(index, 'quantity', parseInt(e.target.value) || 1)}
                      placeholder="Qty"
                      min="1"
                      disabled={isReadOnly}
                      className="w-14 px-2 py-2 rounded-xl border border-[#2C2731]/20 bg-white text-xs focus:border-[#2C2731] focus:outline-none disabled:bg-[#F4EED3] disabled:text-[#2C2731]/50"
                    />
                  </div>
                  <div className="flex items-center gap-1">
                    <input
                      type="number"
                      value={minutesToHoursAndMinutes(component.session_length_minutes).hours}
                      onChange={(e) => {
                        const { minutes } = minutesToHoursAndMinutes(component.session_length_minutes);
                        updateSessionLength(index, parseInt(e.target.value) || 0, minutes);
                      }}
                      placeholder="0"
                      min="0"
                      disabled={isReadOnly}
                      className="w-10 px-1 py-2 rounded-xl border border-[#2C2731]/20 bg-white text-xs text-center focus:border-[#2C2731] focus:outline-none disabled:bg-[#F4EED3] disabled:text-[#2C2731]/50"
                    />
                    <span className="text-xs text-[#2C2731]/70">h</span>
                    <input
                      type="number"
                      value={minutesToHoursAndMinutes(component.session_length_minutes).minutes}
                      onChange={(e) => {
                        const { hours } = minutesToHoursAndMinutes(component.session_length_minutes);
                        updateSessionLength(index, hours, parseInt(e.target.value) || 0);
                      }}
                      placeholder="0"
                      min="0"
                      max="59"
                      disabled={isReadOnly}
                      className="w-10 px-1 py-2 rounded-xl border border-[#2C2731]/20 bg-white text-xs text-center focus:border-[#2C2731] focus:outline-none disabled:bg-[#F4EED3] disabled:text-[#2C2731]/50"
                    />
                    <span className="text-xs text-[#2C2731]/70">m</span>
                  </div>
                  {!isReadOnly && (
                    <button
                      onClick={() => removeComponent(index)}
                      className="p-2 text-[#2C2731]/30 hover:text-[#B9383A] transition-colors ml-auto md:ml-0"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  )}
                </div>
                {/* Meeting link - only for specific guide */}
                {showLinkField && !isReadOnly && (
                  <div className="w-full">
                    <input
                      type="url"
                      value={component.session_link || ''}
                      onChange={(e) => updateComponent(index, 'session_link', e.target.value)}
                      placeholder="Paste meeting link (optional)"
                      className="w-full px-3 py-2 rounded-xl border border-[#2C2731]/20 bg-white text-xs placeholder:text-[#2C2731]/40 focus:border-[#2C2731] focus:outline-none"
                    />
                  </div>
                )}
                {showLinkField && isReadOnly && component.session_link && (
                  <div className="w-full">
                    <span className="text-xs text-[#2C2731]/60">Link: {component.session_link}</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {formData.components.length === 0 && (
          <div className="text-center py-4 text-xs text-[#2C2731]/60 border border-dashed border-[#2C2731]/20 rounded-2xl bg-white/50">
            No components added yet. Click "Add" to create your first component.
          </div>
        )}
      </div>

      {/* Total Cost Display */}
      {formData.components.length > 0 && (
        <div className="mb-4 p-4 bg-gradient-to-r from-[#2C2731] to-[#1F1924] text-[#FFFCEF] rounded-3xl shadow-[0_24px_70px_-40px_rgba(15,8,27,0.85)]">
          <div className="flex justify-between items-center">
            <div>
              <div className="text-sm font-semibold tracking-[0.2em] uppercase text-[#FFFCEF]/70">Total Package Cost</div>
              <div className="text-xs text-[#FFFCEF]/60 mt-1">
                This will be your standard {formData.duration_months}-month offering
              </div>
            </div>
            <div className="text-right">
              <div className="text-2xl font-semibold">${calculateTotalCost().toLocaleString()}</div>
              <div className="text-xs text-[#FFFCEF]/60">
                {formData.components.reduce((sum, comp) => sum + comp.quantity, 0)} sessions total
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Form Actions */}
      <div className="flex justify-end space-x-3">
        <button
          onClick={onCancel}
          disabled={loading}
          className="rounded-full border border-[#2C2731]/30 px-5 py-2 text-sm font-semibold text-[#2C2731] transition hover:bg-[#FFFCEF] disabled:opacity-50"
        >
          {isReadOnly ? 'Close' : 'Cancel'}
        </button>
        {!isReadOnly && (
          <button
            onClick={onSave}
            disabled={!formData.name || formData.components.length === 0 || loading}
            className="rounded-full border border-[#2C2731] bg-[#2C2731] px-5 py-2 text-sm font-semibold text-[#FFFCEF] shadow-[0_15px_40px_-25px_rgba(15,8,27,0.9)] transition hover:-translate-y-0.5 hover:bg-[#241F2A] disabled:bg-[#9F96B5] disabled:border-[#9F96B5] disabled:cursor-not-allowed"
          >
            {loading ? 'Saving...' : isEditing ? 'Save Package' : 'Save Package'}
          </button>
        )}
      </div>
      </div>
    </div>
  );
};

export default PackageForm;
